import { Divider } from "antd";
import type { LucideIcon } from "lucide-react";
import { Briefcase, ScanFace, Store, User } from "lucide-react";

import type { BusinessEntityBindInfo } from "@/api/entities/type";
import { EmptyBlock, SectionTitle } from "@/components/DetailBlocks";
import { cn } from "@/lib/cn";

/** 关联信息 Tab props */
export interface RelatedInfoTabProps {
  /** 详情接口返回的关联信息 */
  bindInfo?: BusinessEntityBindInfo | null;
  /** 点击关联门店，入参为门店 ID */
  onClickShop?: (id: string) => void;
  /** 点击关联代理，入参为代理 ID */
  onClickAgent?: (id: string) => void;
  /** 点击关联账号，入参为用户 ID */
  onClickAccount?: (id: string) => void;
}

/**
 * 关联组织卡片：门店（绿底）/ 代理（蓝底），标题可点击打开详情
 * @param icon 左侧图标
 * @param iconClassName 图标容器样式
 * @param title 主标题
 * @param subtitle 副标题（门店 / 代理）
 * @param onClick 点击标题回调；有则标题可点
 */
const OrgCard = ({
  icon: Icon,
  iconClassName,
  title,
  subtitle,
  onClick,
}: {
  icon: LucideIcon;
  iconClassName: string;
  title: string;
  subtitle: string;
  onClick?: () => void;
}) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
    <div
      className={cn(
        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
        iconClassName,
      )}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      {onClick ? (
        <button
          type="button"
          className="text-sm font-medium hover:underline text-orange-600 cursor-pointer bg-transparent border-0 p-0 truncate max-w-full"
          onClick={onClick}
        >
          {title}
        </button>
      ) : (
        <p className="text-sm font-medium text-slate-800 m-0 truncate">{title}</p>
      )}
      <p className="text-xs text-slate-500 m-0 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

/**
 * 关联信息 Tab：关联组织（门店 + 代理）与关联账号
 * @param bindInfo 详情 bindInfo
 * @param onClickShop 点击门店回调
 * @param onClickAgent 点击代理回调
 * @param onClickAccount 点击关联账号回调
 */
export const RelatedInfoTab = ({
  bindInfo,
  onClickShop,
  onClickAgent,
  onClickAccount,
}: RelatedInfoTabProps) => {
  // 门店列表与代理，一并作为关联组织展示
  const shops = bindInfo?.shops ?? [];
  const agent = bindInfo?.agent;
  const user = bindInfo?.user;
  /** 是否存在任一关联组织 */
  const hasOrgs = shops.length > 0 || Boolean(agent?.agentId);

  return (
    <div className="space-y-5">
      <div>
        <SectionTitle icon={Store} title="关联组织" />
        {hasOrgs ? (
          <div className="grid grid-cols-2 gap-3">
            {shops.map((shop) => (
              <OrgCard
                key={shop.bindId || shop.shopId}
                icon={Store}
                iconClassName="bg-emerald-50 text-emerald-600 border border-emerald-100"
                title={shop.shopName}
                subtitle="门店"
                onClick={shop.shopId && onClickShop ? () => onClickShop(shop.shopId) : undefined}
              />
            ))}
            {agent?.agentName && (
              <OrgCard
                icon={Briefcase}
                iconClassName="bg-blue-50 text-blue-600 border border-blue-100"
                title={agent.agentName}
                subtitle="代理"
                onClick={
                  agent.agentId && onClickAgent ? () => onClickAgent(agent.agentId) : undefined
                }
              />
            )}
          </div>
        ) : (
          <EmptyBlock text="暂无关联组织" />
        )}
      </div>
      <Divider className="my-0" />
      <div>
        <SectionTitle icon={ScanFace} title="关联账号" />
        {user?.userDisplayName ? (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
            <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center border border-orange-100 shrink-0">
              <User className="w-5 h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <button
                type="button"
                className="text-sm font-medium hover:underline text-orange-600 cursor-pointer bg-transparent border-0 p-0"
                onClick={() => user.userId && onClickAccount?.(user.userId)}
              >
                {user.userDisplayName}
              </button>
              <p className="text-xs text-slate-500 m-0 mt-0.5">平台归属自然人</p>
            </div>
          </div>
        ) : (
          <EmptyBlock text="未关联账号" />
        )}
      </div>
    </div>
  );
};
